import { useEffect, useState } from "react";
import { useAuth } from "./AuthContext";

// Mapeo de type_id a rol (debe coincidir con el backend)
const ROLES = {
  ADMIN: "admin",
  ORGANIZER: "organizer",
  TEAMLEADER: "teamleader",
  COUNTRYTEAM: "countryteam",
  OBSERVER: "observer",
};

const USER_TYPES = {
  ADMIN: 1,
  ORGANIZER: 2,
  TEAMLEADER: 3,
  COUNTRYTEAM: 4,
  OBSERVER: 5,
};

const SECTIONS = {
  DASHBOARD: "dashboard",
  SUBMISSION: "submission",
  INITIAL_SUBMISSION: "initial_submission",
  ADMIN_SCENATHON: "admin_scenathon",
  ADMIN_SCENATHON_LIST: "admin_scenathon_list",
  ADMIN_SCENATHON_CREATE: "admin_scenathon_create",
  ADMIN_SCENATHON_COUNTRY_LABELS: "admin_scenathon_country_labels",
  ADMIN_SCENATHON_GAMESLOG: "admin_scenathon_gameslog",
  ADMIN_SCENATHON_DEFINEDSCENARIOS: "admin_scenathon_definedscenarios",
  // ADMIN_SCENATHON_PROCESS: "admin_scenathon_process", // ✅ REMOVIDO
  ADMIN_SCENATHON_USERS: "admin_scenathon_users",
  TRADE_ADJUSTMENT: "trade_adjustment",
  DATA_TRADE_ADJUSTMENT: "data_trade_adjustment",
};

// type_id -> role string
function mapTypeIdToRole(typeId) {
  switch (Number(typeId)) {
    case USER_TYPES.ADMIN:
      return ROLES.ADMIN;
    case USER_TYPES.ORGANIZER:
      return ROLES.ORGANIZER;
    case USER_TYPES.TEAMLEADER:
      return ROLES.TEAMLEADER;
    case USER_TYPES.COUNTRYTEAM:
      return ROLES.COUNTRYTEAM;
    case USER_TYPES.OBSERVER:
    default:
      return ROLES.OBSERVER;
  }
}

// Permisos por type_id (fallback si API no responde)
const TYPE_PERMISSIONS = {
  [USER_TYPES.ADMIN]: [
    SECTIONS.DASHBOARD,
    SECTIONS.SUBMISSION,
    SECTIONS.INITIAL_SUBMISSION,
    SECTIONS.ADMIN_SCENATHON,
    SECTIONS.ADMIN_SCENATHON_LIST,
    SECTIONS.ADMIN_SCENATHON_CREATE,
    SECTIONS.ADMIN_SCENATHON_COUNTRY_LABELS,
    // SECTIONS.ADMIN_SCENATHON_PROCESS, // ✅ REMOVIDO
    SECTIONS.ADMIN_SCENATHON_GAMESLOG,
    SECTIONS.ADMIN_SCENATHON_DEFINEDSCENARIOS,
    SECTIONS.ADMIN_SCENATHON_USERS,
    SECTIONS.TRADE_ADJUSTMENT,
    SECTIONS.DATA_TRADE_ADJUSTMENT,
  ],

  [USER_TYPES.ORGANIZER]: [
    SECTIONS.DASHBOARD,
    SECTIONS.SUBMISSION,
    SECTIONS.INITIAL_SUBMISSION,
    SECTIONS.ADMIN_SCENATHON,
    // SECTIONS.ADMIN_SCENATHON_PROCESS, // ✅ REMOVIDO
    SECTIONS.TRADE_ADJUSTMENT,
    SECTIONS.DATA_TRADE_ADJUSTMENT,
  ],

  [USER_TYPES.TEAMLEADER]: [
    SECTIONS.DASHBOARD,
    SECTIONS.SUBMISSION,
    SECTIONS.INITIAL_SUBMISSION,
    SECTIONS.TRADE_ADJUSTMENT,
    SECTIONS.DATA_TRADE_ADJUSTMENT,
  ],

  [USER_TYPES.COUNTRYTEAM]: [
    SECTIONS.DASHBOARD,
    SECTIONS.SUBMISSION,
    SECTIONS.INITIAL_SUBMISSION,
    SECTIONS.TRADE_ADJUSTMENT,
    SECTIONS.DATA_TRADE_ADJUSTMENT,
  ],

  [USER_TYPES.OBSERVER]: [
    SECTIONS.DASHBOARD,
    SECTIONS.SUBMISSION,
    SECTIONS.INITIAL_SUBMISSION,
  ],
};

let sectionPermissionsCache = null;
let sectionPermissionsPromise = null;

function normalizePermissionsMap(payload) {
  const out = {};
  if (!payload || typeof payload !== "object") return out;

  Object.entries(payload).forEach(([typeId, sections]) => {
    const key = Number(typeId);
    if (!Number.isInteger(key)) return;
    out[key] = Array.isArray(sections)
      ? Array.from(new Set(sections.map((s) => String(s || "").trim()).filter(Boolean)))
      : [];
  });

  return out;
}

/**
 * Hook para acceder a funciones de roles y permisos
 * @returns {Object} Objeto con funciones y datos de rol
 */
export function useRole() {
  const { user, authFetch } = useAuth();
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const [, setPermissionsVersion] = useState(0);

  useEffect(() => {
    if (!user || !authFetch) return;

    let mounted = true;

    if (!sectionPermissionsCache && !sectionPermissionsPromise) {
      sectionPermissionsPromise = authFetch("/api/auth/section-permissions")
        .then(async (res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json().catch(() => ({}));
          sectionPermissionsCache = normalizePermissionsMap(data?.permissionsByType);
        })
        .catch(() => {
          sectionPermissionsCache = null;
        })
        .finally(() => {
          sectionPermissionsPromise = null;
        });
    }

    const syncPermissions = () => {
      if (mounted) {
        setPermissionsLoaded(true);
        setPermissionsVersion((v) => v + 1);
      }
    };

    if (sectionPermissionsPromise) {
      sectionPermissionsPromise.finally(syncPermissions);
    } else {
      setPermissionsLoaded(true);
    }

    const onPermissionsUpdated = (event) => {
      const next = normalizePermissionsMap(event?.detail?.permissionsByType);
      if (Object.keys(next).length > 0) {
        sectionPermissionsCache = next;
        syncPermissions();
      }
    };

    window.addEventListener("section-permissions-updated", onPermissionsUpdated);

    return () => {
      mounted = false;
      window.removeEventListener("section-permissions-updated", onPermissionsUpdated);
    };
  }, [authFetch, user]);

  const getPermissionsForType = (typeId) => {
    const numericTypeId = Number(typeId);
    if (sectionPermissionsCache?.[numericTypeId]) {
      return sectionPermissionsCache[numericTypeId];
    }
    return TYPE_PERMISSIONS[numericTypeId] || [];
  };

  // Obtener el rol del usuario actual
  const getUserRole = () => {
    if (!user || !user.type_id) return ROLES.OBSERVER;
    return mapTypeIdToRole(user.type_id);
  };

  // Verificar si usuario tiene un rol específico (puede ser exact o admin)
  const hasRole = (requiredRole) => {
    if (!user || !user.type_id) return false;
    const userRole = getUserRole();
    // ADMIN siempre tiene acceso
    if (userRole === ROLES.ADMIN) return true;
    // Verificación exacta
    return userRole === requiredRole;
  };

  // Verificar si usuario tiene alguno de varios roles
  const hasAnyRole = (roles) => {
    if (!Array.isArray(roles)) return false;
    return roles.some((role) => hasRole(role));
  };

  // Verificar si usuario tiene todos los roles
  const hasAllRoles = (roles) => {
    if (!Array.isArray(roles)) return false;
    return roles.every((role) => hasRole(role));
  };

  // Verificar acceso a una sección
  const hasAccessToSection = (section) => {
    if (!user || !user.type_id) return false;
    const permissions = getPermissionsForType(user.type_id);
    return permissions.includes(section);
  };

  // Obtener todas las secciones a las que el usuario tiene acceso
  const getAccessibleSections = () => {
    if (!user || !user.type_id) return [];
    return getPermissionsForType(user.type_id);
  };

  // Verificar si es admin
  const isAdmin = () => getUserRole() === ROLES.ADMIN;

  // Verificar si es organizador
  const isOrganizer = () => hasRole(ROLES.ORGANIZER);

  // Verificar si es team leader
  const isTeamLeader = () => hasRole(ROLES.TEAMLEADER);

  // Verificar si es country team
  const isCountryTeam = () => hasRole(ROLES.COUNTRYTEAM);

  // Verificar si es observer
  const isObserver = () => hasRole(ROLES.OBSERVER);

  return {
    // Funciones de verificación
    hasRole,
    hasAnyRole,
    hasAllRoles,
    hasAccessToSection,
    getUserRole,
    getAccessibleSections,
    // Funciones específicas por rol
    isAdmin,
    isOrganizer,
    isTeamLeader,
    isCountryTeam,
    isObserver,
    // Constantes
    ROLES,
    SECTIONS,
    // Estado de carga
    permissionsLoaded,
  };
}
